<template>
  <data-input
    ref="project"
    v-model="project"
    :data-type="{ name: 'select' }"
    :items="projectsList"
    :menu-props="{
      offsetY: true,
      nudgeBottom: 35,
      minWidth: 250,
    }"
    :attach="true"
    :auto-convert-select="false"
    @menu:toggle="onMenuToggle"
    class="tw:hidden-select-activator tw:[&_.v-list]:pt-0!"
  >
    <template #item="{ on, attrs, item }">
      <v-list-item v-on="on" v-bind="attrs" @click="zoomToProject(item.value)">
        <v-list-item-content>
          <v-list-item-title>
            {{ item.text }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-if="areProjectsLoading" #[`no-data`]>
      <div class="tw:py-2.5">
        <main-loader />
      </div>
    </template>
    <template #[`prepend-item`]>
      <div class="px-3 pt-3 pb-2 tw:sticky tw:top-0 tw:z-1 tw:bg-white">
        <data-input
          ref="filter"
          :data-type="{ name: 'text' }"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$i18n.t('default.find')"
          hide-details
          clearable
          class="tw:solo-input"
          @input="debouncedOnFilterInput"
        />
      </div>
    </template>
    <template #prepend>
      <div class="tw:w-40">
        <base-button
          type="alternative"
          size="sm"
          :toggled="!!project"
          @click="onProjectsClick"
          class="tw:w-full tw:[&_.v-btn\_\_content]:w-full tw:[&_.text-slot]:w-full tw:[&_.text-slot]:min-w-0"
        >
          <div class="tw:text-left tw:truncate">
            {{ currentProjectName ?? $i18n.t('navbar.project') }}
          </div>
          <template #appendIcon>
            <div class="tw:flexrow-0.5 tw:items-center tw:shrink-0">
              <v-progress-circular
                v-if="isZoomLoading"
                :size="13"
                :width="2"
                indeterminate
                class="tw:mr-0.5"
              ></v-progress-circular>
              <v-icon v-if="project" ref="close" small @click="project = null"> mdi-close </v-icon>
              <v-icon small class="expand-icon">mdi-chevron-down</v-icon>
            </div>
          </template>
        </base-button>
      </div>
    </template>
  </data-input>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import { GeoJSON } from 'ol/format';
import { debounce } from 'lodash';

import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';

export default {
  name: 'OzeProjectFilterSelector',
  components: {
    DataInput,
    MainLoader,
  },
  data: () => ({
    projectsList: [],
    areProjectsLoading: false,

    isZoomLoading: false,
  }),
  computed: {
    ozeProjectsNameAttribute: get('layers/metadata@:ozeProjectsDatasourceName?.attributes_schema.desc_attribute_name'),
    ozeProjectsDatasourceName: get('admin/modulesMapping@oze.oze_projects_datasource.datasource'),
    ozeProjectsDatasourceIdName: get('layers/metadata@:ozeProjectsDatasourceName?.attributes_schema.id_name'),
    project: sync('layers/ozeFilterProjectId'),
    layersFilters: get('layers/layersFilters'),
    currentProjectName() {
      if (!this.project) return;
      return this.projectsList.find(p => p.value === this.project)?.text;
    },
    projectLayers: get('layers/flatProjectLayers'),
  },
  methods: {
    getDatasourceFeatures: call('layers/getV2DatasourceFeatures'),
    getRelationsGeometry: call('relations/getRelationsGeometry'),
    async fetchProjects() {
      if (this.projectsList.length) return;
      this.areProjectsLoading = true;
      try {
        const projects = await this.getDatasourceFeatures({
          datasource: this.ozeProjectsDatasourceName,
          payload: {
            attributes: [this.ozeProjectsNameAttribute],
          },
          params: {
            with_geometry: false,
          },
        });
        this.projectsList = projects.features.map(project => ({
          value: project.id,
          text: project.properties[this.ozeProjectsNameAttribute],
        }));
        this.rawProjectsList = [...this.projectsList];
      } catch (error) {
        console.error(error);
      } finally {
        this.areProjectsLoading = false;
      }
    },
    onProjectsClick(e) {
      if (e.target === this.$refs.close?.$el || this.$refs.close?.$el?.contains(e.target)) return;
      this.$refs.project?.$refs['data-input']?.toggleMenu();
      this.fetchProjects();
    },
    async zoomToProject(projectId) {
      if (!projectId) return;
      this.isZoomLoading = true;
      try {
        const r = await this.getRelationsGeometry({
          datasource: this.ozeProjectsDatasourceName,
          payload: {
            features_filter: {
              $EQUAL: {
                [`${this.ozeProjectsDatasourceName}.${this.ozeProjectsDatasourceIdName}`]: {
                  value: projectId,
                },
              },
            },
          },
        });
        const feature = r?.features?.features?.[0];
        if (!feature) return;
        const olFeature = new GeoJSON().readFeature(feature);
        this.$root.$emit('fitView', olFeature.getGeometry().getExtent());
      } catch (error) {
        console.error(error);
      } finally {
        this.isZoomLoading = false;
      }
    },
    onFilterInput(e) {
      if (!e) return (this.projectsList = [...this.rawProjectsList]);
      this.projectsList = this.rawProjectsList.filter(
        item => item.value === this.project || ('' + (item.text ?? '')).toLowerCase().includes((e ?? '').toLowerCase())
      );
    },
    onMenuToggle(isOpen) {
      if (isOpen) return;
      this.$refs.filter.$refs['data-input'].$refs.input.$data.lazyValue = '';
      this.onFilterInput();
    },
  },
  created() {
    this.debouncedOnFilterInput = debounce(this.onFilterInput, 575);
  },
};
</script>
